import { useEffect, useState } from "react";

import { isNotEmpty } from "@/libs/utils";

import { BidSimilarDetailContent, BidSimilarItem } from "@/types/bid";

import { GetBidSimilarDetail } from "@/services/Bid";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

import LinkIcon from "@/assets/images/BidDetail/link-icon.png";
import CommonImg from "@/components/Common/Img";

type Props = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  bid: BidSimilarItem | null;
};
export default function DialogBidSimilarDetail({ open, setOpen, bid }: Props) {
  const [bidDetail, setBidDetail] = useState<BidSimilarDetailContent | null>(
    null
  );

  const getBidSimilarDetail = async () => {
    try {
      if (!isNotEmpty(bid)) {
        return;
      }
      const result = await GetBidSimilarDetail(bid?.contract_id);
      setBidDetail(result.data.pods[0].content);
    } catch (e) {
    } finally {
    }
  };

  useEffect(() => {
    if (open) {
      getBidSimilarDetail();
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger></DialogTrigger>
      <DialogContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        className="max-w-[1000px]"
      >
        <DialogHeader>
          <DialogTitle className="text-left text-[20px]">
            {bidDetail?.contract_info?.contract_name}
          </DialogTitle>
          <DialogClose />
        </DialogHeader>
        <DialogDescription />
        {bidDetail && (
          <div className="flex flex-col gap-[16px] text-[14px]">
            <div className="flex gap-[20px]">
              <div className="flex gap-[14px]">
                <div className="font-semibold">공고 번호</div>
                <div className="text-gray-B">
                  {bidDetail?.contract_info?.notice_no}
                </div>
              </div>
              <div className="flex gap-[14px]">
                <div className="font-semibold">공고 차수</div>
                <div className="text-gray-B">
                  {bidDetail?.contract_info?.decision_contract_ord}
                </div>
              </div>
              <div className="flex gap-[14px]">
                <div className="font-semibold">계약 번호</div>
                <div className="text-gray-B">
                  {bidDetail?.contract_info?.contract_id}
                </div>
              </div>
            </div>
            <div className="flex justify-between">
              <div className="flex gap-[14px]">
                <div className="font-semibold">수요기관 담당자 정보</div>
                <div className="text-gray-B">
                  {bidDetail?.contact_info?.manager_name}
                </div>
                <div className="text-dark-blue">
                  {bidDetail?.contact_info?.manager_phone}
                </div>
              </div>
              {bidDetail?.contact_info?.url && (
                <div
                  className="flex gap-[4px] items-center text-[12px] font-thin cursor-pointer"
                  onClick={() => {
                    window.open(bidDetail?.contact_info?.url);
                  }}
                >
                  <CommonImg
                    src={LinkIcon}
                    alt="Link"
                    className="size-[20px]"
                  />
                  상세정보 원문보기
                </div>
              )}
            </div>
            <div className="max-h-[600px] overflow-auto flex flex-col gap-[40px]">
              <div className="flex flex-col gap-[8px]">
                <div className="text-[18px] font-bold">낙찰 기업 정보</div>
                <table className="w-full border-[1px]">
                  <colgroup>
                    <col width="45%" />
                    <col width="30%" />
                    <col width="10%" />
                    <col width="15%" />
                  </colgroup>
                  <thead>
                    <tr className="h-[30px] bg-dark-brown text-white text-[12px] font-semibold">
                      <td className="text-left pl-[10px]">낙찰 기업명</td>
                      <td className="text-center">사업자등록번호</td>
                      <td className="text-center">대표자명</td>
                      <td className="text-center">낙찰 금액</td>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    <tr className="h-[50px]">
                      <td className="text-left pl-[10px]">
                        {bidDetail?.contract_info?.corp_name}
                      </td>
                      <td className="text-center">
                        {bidDetail?.contract_info?.corp_no}
                      </td>
                      <td className="text-center">
                        {bidDetail?.contract_info?.ceo_name}
                      </td>
                      <td className="text-center">
                        {`${bidDetail?.contract_info?.contract_amount?.toLocaleString()}원`}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="flex flex-col gap-[8px]">
                <div className="text-[18px] font-bold">
                  계약 체결 및 수행 기업 정보
                </div>
                <table className="w-full border-[1px]">
                  <colgroup>
                    <col width="5%" />
                    <col width="35%" />
                    <col width="20%" />
                    <col width="10%" />
                    <col width="10%" />
                    <col width="10%" />
                    <col width="10%" />
                  </colgroup>
                  <thead>
                    <tr className="h-[30px] bg-dark-brown text-white text-[12px] font-semibold">
                      <td className="text-center">#</td>
                      <td className="text-left">
                        사업 수행 업체 (컨소시움 구성)
                      </td>
                      <td className="text-center">사업자등록번호</td>
                      <td className="text-center">대표자명</td>
                      <td className="text-center">계약주체</td>
                      <td className="text-center">구조</td>
                      <td className="text-center">분담률</td>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {bidDetail?.company_info.map((company, companyIndex) => (
                      <tr
                        key={companyIndex}
                        className="h-[50px] border-t-2 border-gray-D"
                      >
                        <td className="text-center">{companyIndex + 1}</td>
                        <td className="text-left">{company.corp_name}</td>
                        <td className="text-center">{company.corp_no}</td>
                        <td className="text-center">{company.ceo_name}</td>
                        <td className="text-center">{company.contract_type}</td>
                        <td className="text-center">
                          {company.contract_structure}
                        </td>
                        <td className="text-center">
                          {isNotEmpty(company.share_ratio)
                            ? `${company.share_ratio * 100}%`
                            : ""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-col gap-[8px]">
                <div className="flex justify-between">
                  <div className="text-[18px] font-bold">경쟁 입찰 정보</div>
                  <div className="text-[12px]">
                    <span className="text-dark-brown font-semibold">
                      {bidDetail?.bidding_summary?.num_bidding_company}
                    </span>
                    개 기업 입찰 |&nbsp;
                    <span className="text-dark-brown font-semibold">
                      {bidDetail?.bidding_summary?.num_qualified_bidding}
                    </span>
                    개 기업 유효 경쟁 |&nbsp;
                    <span className="text-red font-semibold">
                      {bidDetail?.bidding_summary?.num_ineligible_bidding}
                    </span>
                    개 기업&nbsp;부적격 판정
                  </div>
                </div>
                <table className="w-full border-[1px]">
                  <colgroup>
                    <col width="5%" />
                    <col width="25%" />
                    <col width="10%" />
                    <col width="10%" />
                    <col width="10%" />
                    <col width="10%" />
                    <col width="10%" />
                    <col width="10%" />
                    <col width="10%" />
                  </colgroup>
                  <thead>
                    <tr className="h-[30px] bg-dark-brown text-white text-[12px] font-semibold">
                      <td className="text-center">순위</td>
                      <td className="text-left">경쟁 참가 업체명</td>
                      <td className="text-center">사업자등록번호</td>
                      <td className="text-center">대표자명</td>
                      <td className="text-center">입찰금액(원)</td>
                      <td className="text-center">투찰율(%)</td>
                      <td className="text-center">입찰 가격점수</td>
                      <td className="text-center">기술 평가점수</td>
                      <td className="text-center">종합평점</td>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {bidDetail?.bidding_info.map((bid, bidIndex) => (
                      <tr
                        key={bidIndex}
                        className="h-[50px] border-t-2 border-gray-D"
                      >
                        <td className="text-center">{bid.rank}</td>
                        <td className="text-left">{bid.corp_name}</td>
                        <td className="text-center">{bid.corp_no}</td>
                        <td className="text-center">{bid.ceo_name}</td>
                        <td className="text-center">
                          {bid.bid_amount?.toLocaleString()}
                        </td>
                        <td className="text-center">
                          {isNotEmpty(bid.bidding_rate)
                            ? `${bid.bidding_rate * 100}%`
                            : ""}
                        </td>
                        <td className="text-center">
                          {bid.bidding_price_score}
                        </td>
                        <td className="text-center">{bid.skill_score}</td>
                        <td className="text-center">{bid.overall_score}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
